import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { motion } from "framer-motion";
import { DollarSign, Users, TrendingUp, CreditCard, TrendingDown, Wallet, Clock } from "lucide-react";

export default function ManagerBilling() {
  const { data: billing, isLoading: billingLoading } = useQuery({
    queryKey: ["admin_stats_stripe_billing"],
    queryFn: async () => {
      const { data, error } = await supabase.functions.invoke("admin-stats", {
        body: { section: "billing" },
      });
      if (error) throw error;
      return data;
    },
  });

  const { data: accountsData } = useQuery({
    queryKey: ["admin_stats_accounts_billing"],
    queryFn: async () => {
      const { data, error } = await supabase.functions.invoke("admin-stats", {
        body: { section: "accounts" },
      });
      if (error) throw error;
      return data?.accounts || [];
    },
  });

  const accounts = accountsData || [];
  const activeCount = accounts.filter((a: any) => a.subscription_status === "active").length;
  const trialCount = accounts.filter((a: any) => a.subscription_status === "trialing").length;
  const canceledCount = accounts.filter((a: any) => a.subscription_status === "canceled").length;
  const freeCount = accounts.filter((a: any) => !a.subscription_status || a.subscription_status === "trialing").length;

  const fmtUsd = (value: number | undefined) => {
    if (value === undefined || value === null) return "—";
    return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(value);
  };

  const fmtBrl = (value: number | undefined) => {
    if (value === undefined || value === null) return null;
    const rate = billing?.usdToBrl;
    if (!rate) return null;
    return new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(value * rate);
  };

  const dualFmt = (value: number | undefined) => {
    const usd = fmtUsd(value);
    const brl = fmtBrl(value);
    return { usd, brl };
  };

  const stripeCards = [
    { label: "MRR (Receita Mensal Recorrente)", value: billing ? dualFmt(billing.mrr) : null, icon: TrendingUp, accent: "text-accent" },
    { label: "Receita (últimos 30 dias)", value: billing ? dualFmt(billing.totalRevenue30d) : null, icon: DollarSign, accent: "text-primary" },
    { label: "Saldo Disponível", value: billing ? dualFmt(billing.availableBalance) : null, icon: Wallet, accent: "text-green-400" },
    { label: "Saldo Pendente", value: billing ? dualFmt(billing.pendingBalance) : null, icon: Clock, accent: "text-yellow-400" },
  ];

  const statusCards = [
    { label: "Assinaturas Ativas (Stripe)", value: billing?.activeSubscriptions, icon: CreditCard, accent: "text-accent" },
    { label: "Em Trial (Stripe)", value: billing?.trialSubscriptions, icon: Users, accent: "text-blue-400" },
    { label: "Cobranças (30 dias)", value: billing?.chargesCount30d, icon: TrendingUp, accent: "text-primary" },
    { label: "Reembolsos (30 dias)", value: billing ? dualFmt(billing.totalRefunded30d) : null, icon: TrendingDown, accent: "text-destructive" },
  ];

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-display text-2xl md:text-3xl font-bold text-foreground">Faturamento</h1>
        <div className="flex items-center gap-3 mt-1">
          <p className="text-muted-foreground text-sm">
            Dados financeiros em tempo real do Stripe
          </p>
          {billing?.usdToBrl && (
            <span className="text-[11px] px-2 py-0.5 rounded-full bg-secondary/60 text-muted-foreground">
              Cotação: $1 = R$ {billing.usdToBrl.toFixed(2)}
            </span>
          )}
        </div>
      </div>

      {/* Stripe Revenue Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {stripeCards.map((c, i) => (
          <motion.div
            key={c.label}
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: i * 0.06 }}
          >
            <Card className="glass-card border-warmz-glass-border/50">
              <CardContent className="flex items-center gap-4 p-5">
                <div className="w-11 h-11 rounded-xl bg-secondary/60 flex items-center justify-center">
                  <c.icon className={`h-5 w-5 ${c.accent}`} />
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">{c.label}</p>
                  <p className="font-display text-xl font-bold text-foreground">
                    {billingLoading ? <Skeleton className="h-6 w-20" /> : (
                      typeof c.value === "object" && c.value !== null
                        ? (c.value as { usd: string; brl: string | null }).usd
                        : (String(c.value ?? "—"))
                    )}
                  </p>
                  {typeof c.value === "object" && c.value?.brl && (
                    <p className="text-[10px] text-muted-foreground/70 mt-0.5">≈ {c.value.brl}</p>
                  )}
                </div>
              </CardContent>
            </Card>
          </motion.div>
        ))}
      </div>

      {/* Status Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {statusCards.map((c, i) => (
          <motion.div
            key={c.label}
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.25 + i * 0.06 }}
          >
            <Card className="glass-card border-warmz-glass-border/50">
              <CardContent className="flex items-center gap-4 p-5">
                <div className="w-11 h-11 rounded-xl bg-secondary/60 flex items-center justify-center">
                  <c.icon className={`h-5 w-5 ${c.accent}`} />
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">{c.label}</p>
                  <p className="font-display text-xl font-bold text-foreground">
                    {billingLoading ? <Skeleton className="h-6 w-12" /> : (
                      typeof c.value === "object" && c.value !== null
                        ? c.value.usd
                        : (c.value ?? "—")
                    )}
                  </p>
                  {typeof c.value === "object" && c.value?.brl && (
                    <p className="text-[10px] text-muted-foreground/70 mt-0.5">≈ {c.value.brl}</p>
                  )}
                </div>
              </CardContent>
            </Card>
          </motion.div>
        ))}
      </div>

      {/* Recent Charges */}
      {billing?.recentCharges?.length > 0 && (
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.5 }}>
          <Card className="glass-card border-warmz-glass-border/50">
            <CardHeader>
              <CardTitle className="text-lg font-display">Cobranças Recentes</CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-warmz-glass-border/30">
                      <th className="text-left p-3 text-muted-foreground font-medium">Data</th>
                      <th className="text-left p-3 text-muted-foreground font-medium">Valor</th>
                      <th className="text-left p-3 text-muted-foreground font-medium">Status</th>
                      <th className="text-left p-3 text-muted-foreground font-medium">Descrição</th>
                    </tr>
                  </thead>
                  <tbody>
                    {billing.recentCharges.map((charge: any) => (
                      <tr key={charge.id} className="border-b border-warmz-glass-border/20">
                        <td className="p-3 text-foreground">
                          {new Date(charge.created * 1000).toLocaleDateString("pt-BR")}
                        </td>
                        <td className="p-3 text-foreground font-medium">
                          {fmtUsd(charge.amount)}
                          {fmtBrl(charge.amount) && (
                            <span className="block text-[10px] text-muted-foreground/70">≈ {fmtBrl(charge.amount)}</span>
                          )}
                        </td>
                        <td className="p-3">
                          <span className="px-2 py-0.5 rounded-full text-xs bg-accent/20 text-accent">
                            {charge.status}
                          </span>
                        </td>
                        <td className="p-3 text-muted-foreground text-xs truncate max-w-[200px]">
                          {charge.description || "—"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </motion.div>
      )}

      {/* Distribution */}
      <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.6 }}>
        <Card className="glass-card border-warmz-glass-border/50">
          <CardContent className="p-5 space-y-4">
            <h2 className="font-display text-lg font-semibold text-foreground">Distribuição de Status (BD local)</h2>
            <div className="space-y-3">
              {[
                { label: "Ativo", count: activeCount, color: "bg-accent" },
                { label: "Trial", count: trialCount, color: "bg-blue-400" },
                { label: "Cancelado", count: canceledCount, color: "bg-destructive" },
                { label: "Free/Sem plano", count: freeCount, color: "bg-muted-foreground" },
              ].map((item) => {
                const total = accounts.length || 1;
                const pct = Math.round((item.count / total) * 100);
                return (
                  <div key={item.label} className="space-y-1">
                    <div className="flex justify-between text-sm">
                      <span className="text-foreground font-medium">{item.label}</span>
                      <span className="text-muted-foreground">{item.count} ({pct}%)</span>
                    </div>
                    <div className="h-2 rounded-full bg-secondary/60 overflow-hidden">
                      <div
                        className={`h-full rounded-full ${item.color} transition-all`}
                        style={{ width: `${pct}%` }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      </motion.div>
    </div>
  );
}
